import { CheckCircle, XCircle } from "lucide-react";
import { useEffect } from "react";
import { createPortal } from "react-dom";

interface StatusModalProps {
  isOpen: boolean;
  type: "success" | "error" | "waiting";
  message?: string;
  title?: string;
  onClose: () => void;
  autoHideDuration?: number; // in milliseconds
}

export const StatusModal: React.FC<StatusModalProps> = ({
  isOpen,
  type,
  message,
  title,
  onClose,
  autoHideDuration = 3000,
}) => {
  useEffect(() => {
    if (isOpen && autoHideDuration > 0 && type !== "waiting") {
      const timer = setTimeout(() => {
        onClose();
      }, autoHideDuration);

      return () => clearTimeout(timer);
    }
  }, [isOpen, autoHideDuration, onClose, type]);

  if (!isOpen) return null;

  // The headline. success/error carry a sensible type label; "waiting" has NO
  // default — the caller MUST pass a real status (e.g. the live phase). If neither
  // a title nor a message is given, there's nothing real to show → throw (no
  // fabricated fallback like "Building model" / "Processing…").
  const resolvedTitle =
    title ?? (type === "success" ? "Success!" : type === "error" ? "Error" : undefined);
  if (!resolvedTitle && !message) {
    throw new Error(
      "StatusModal: a `title` or `message` is required (type='waiting' has no default status text).",
    );
  }

  const modalContent = (
    <div
      className="fixed inset-0 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]"
      style={{
        background: 'var(--modal-backdrop)',
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        contain: "layout style paint",
      }}
    >
      <div
        className="bg-[var(--modal-bg)] rounded-xl w-full max-w-lg p-8 shadow-2xl border border-[var(--border-primary)]"
        style={{
          contain: "layout style paint",
        }}
      >
        <div className="text-center py-4">
          <div
            className={`w-16 h-16 ${
              type === "success"
                ? "bg-green-500/20"
                : type === "error"
                ? "bg-red-500/20"
                : "bg-blue-500/20"
            } rounded-full flex items-center justify-center mx-auto mb-4 relative`}
          >
            {type === "success" ? (
              <CheckCircle className="w-8 h-8 text-green-500" />
            ) : type === "error" ? (
              <XCircle className="w-8 h-8 text-red-500" />
            ) : (
              <>
                {/* Pulsing rings */}
                <div className="absolute inset-0 rounded-full bg-blue-500/30 animate-ping" />
                <div
                  className="absolute inset-0 rounded-full bg-blue-500/20 animate-pulse"
                  style={{ animationDelay: "150ms" }}
                />
                {/* Animated dots */}
                <div className="relative flex gap-1">
                  <div
                    className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                    style={{ animationDelay: "0ms" }}
                  />
                  <div
                    className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"
                    style={{ animationDelay: "150ms" }}
                  />
                  <div
                    className="w-2 h-2 bg-blue-300 rounded-full animate-bounce"
                    style={{ animationDelay: "300ms" }}
                  />
                </div>
              </>
            )}
          </div>
          {resolvedTitle && (
            <h3 style={{ color: 'var(--text-primary)' }} className="text-lg font-medium mb-2">
              {resolvedTitle}
            </h3>
          )}
          {message && (
            <p
              style={{ color: 'var(--text-secondary)' }}
              className="text-sm whitespace-pre-wrap break-words max-h-52 overflow-auto text-left"
            >
              {message}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  // Use React Portal to render outside normal DOM hierarchy
  return createPortal(modalContent, document.body);
};
